import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Input, Button, CustomSelect, Label, FormField, FormError } from '@redpanda/ui';
import { useServiceStore } from '../stores/useServiceStore';
import { useStaffStore } from '../stores/useStaffStore';
import { Booking } from '../stores/useBookingStore';
import { useTranslation } from 'react-i18next';
import TimeSlotSelector from './TimeSlotSelector';
import { useCurrentStore } from '../hooks/useCurrentStore';
import { bookingApi } from '../services/api';

interface BookingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  booking?: Booking | null;
}

interface FormData {
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  serviceId: string;
  staffId?: string;
  date: string;
  status?: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  note?: string;
}

const BookingDialog: React.FC<BookingDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  booking,
}) => {
  const { t } = useTranslation(['pages', 'translation']);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>();

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ✅ 時段選擇功能
  const [useTimeSlotSelector, setUseTimeSlotSelector] = useState(false); // 預設關閉，編輯模式不使用
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ time: string; displayTime: string } | null>(null);

  const { storeId } = useCurrentStore();
  const services = useServiceStore((state) => state.services.filter(s => s.isActive));
  const staff = useStaffStore((state) => state.staff.filter(s => s.isActive));

  const isEditMode = !!booking;

  // 監聽下拉選單值
  const watchedServiceId = watch('serviceId', '');
  const watchedStaffId = watch('staffId', '');
  const watchedStatus = watch('status', 'PENDING');

  // ✅ 自動尋找下一個有可用時段的日期（用於新增模式）
  const findNextAvailableDateForNewBooking = async () => {
    if (!watchedServiceId || !storeId) {
      // 如果還沒選擇服務，預設使用今天
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setSelectedDate(dateStr);
      return;
    }

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const maxDaysToCheck = 7;
    for (let i = 0; i < maxDaysToCheck; i++) {
      const checkDate = new Date();
      checkDate.setDate(checkDate.getDate() + i);
      const dateStr = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;

      try {
        const response = await bookingApi.getAvailableSlots({
          storeId,
          serviceId: watchedServiceId,
          date: dateStr,
          staffId: watchedStaffId || undefined,
        });

        let slots = response.data.availableSlots || [];

        // ✅ 如果是今天，過濾掉已經過去的時段
        if (dateStr === todayStr) {
          const currentHour = now.getHours();
          const currentMinute = now.getMinutes();

          slots = slots.filter((timeStr: string) => {
            const [slotHour, slotMinute] = timeStr.split(':').map(Number);
            // 只保留未來的時段（至少30分鐘後）
            return (
              slotHour > currentHour ||
              (slotHour === currentHour && slotMinute > currentMinute + 30)
            );
          });
        }

        if (slots.length > 0) {
          setSelectedDate(dateStr);
          return;
        }
      } catch (error) {
        console.error(`Failed to check slots for ${dateStr}:`, error);
      }
    }

    // 如果7天內都沒有可用時段，還是設定為今天
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setSelectedDate(dateStr);
  };

  // 設置表單初始值
  useEffect(() => {
    if (isOpen) {
      if (booking && booking.customer) {
        // 編輯模式（完整的 booking 對象）
        setUseTimeSlotSelector(false); // 編輯模式不使用時段選擇器
        setValue('customerName', booking.customer.name);
        setValue('customerEmail', booking.customer.email || '');
        setValue('customerPhone', booking.customer.phone || '');
        setValue('serviceId', booking.serviceId);
        setValue('staffId', booking.staffId || '');
        setValue('date', new Date(booking.date).toISOString().slice(0, 16));
        setValue('status', booking.status);
        setValue('note', booking.note || '');
        setSelectedDate('');
        setSelectedTimeSlot(null);
      } else {
        // 新增模式 - 啟用時段選擇器
        setUseTimeSlotSelector(true);
        setSelectedTimeSlot(null);

        // ✅ 先設定預設日期為今天（選擇服務後會自動更新）
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        let localDateTime: string;
        if (booking && booking.date) {
          // 有預填日期（從日曆時間槽點擊）
          localDateTime = new Date(booking.date).toISOString().slice(0, 16);
          // 同時設定選擇的日期
          const prefilledDate = new Date(booking.date);
          const prefilledDateStr = `${prefilledDate.getFullYear()}-${String(prefilledDate.getMonth() + 1).padStart(2, '0')}-${String(prefilledDate.getDate()).padStart(2, '0')}`;
          setSelectedDate(prefilledDateStr);
        } else {
          // 預設日期為今天
          setSelectedDate(todayStr);
          // 預設時間為現在
          const now = new Date();
          localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        }

        reset({
          customerName: '',
          customerEmail: '',
          customerPhone: '',
          serviceId: '',
          staffId: '',
          date: localDateTime,
          note: '',
        });
      }
    }
  }, [isOpen, booking, setValue, reset]);

  // ✅ 當服務或員工選擇改變時，重新尋找可用日期
  useEffect(() => {
    if (useTimeSlotSelector && watchedServiceId) {
      findNextAvailableDateForNewBooking();
    }
  }, [watchedServiceId, watchedStaffId]);

  // 當選擇時段時，自動填入日期時間欄位
  const handleSlotSelect = (slot: { time: string; displayTime: string }) => {
    setSelectedTimeSlot(slot);
    // 將選擇的時段轉換為 datetime-local 格式
    const dateTimeStr = slot.time.slice(0, 16); // 'YYYY-MM-DDTHH:mm'
    setValue('date', dateTimeStr);
  };

  const handleClose = () => {
    setSubmitError(null);
    setSelectedDate('');
    setSelectedTimeSlot(null);
    reset();
    onClose();
  };

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleFormSubmit = async (data: FormData) => {
    // 手動驗證必填欄位（用於 CustomSelect）
    if (!watchedServiceId) {
      setValue('serviceId', '', { shouldValidate: true });
      return;
    }

    // ✅ 修正時區問題：datetime-local 輸入的是本地時間，需要轉換為 UTC
    const localDate = new Date(data.date);
    const isoStringWithLocalTime = localDate.toISOString();

    // 將空字串轉換為 undefined（避免後端驗證錯誤）
    const submitData = {
      ...data,
      date: isoStringWithLocalTime,
      customerEmail: data.customerEmail && data.customerEmail.trim() !== '' ? data.customerEmail : undefined,
      staffId: data.staffId && data.staffId !== '' ? data.staffId : undefined,
      note: data.note && data.note.trim() !== '' ? data.note : undefined,
    };

    if (isEditMode) {
      // 編輯模式：立即關閉（樂觀更新在 store 處理）
      handleClose();
      onSubmit(submitData).catch(() => {
        // 錯誤處理與 UI 回滾在 store 中完成
      });
    } else {
      // 新增模式：等待 API（時段驗證需確認成功）
      setLoading(true);
      setSubmitError(null);
      try {
        await onSubmit(submitData);
        handleClose();
      } catch (error: any) {
        const errorMessage = error.response?.data?.message ||
                            error.response?.data?.errors?.[0]?.msg ||
                            t('dialogs.booking.operationFailed', { ns: 'pages' });
        setSubmitError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  // 生成服務選項
  const serviceOptions = services.map((service) => ({
    value: service.id,
    label: `${service.name} - $${Math.floor(Number(service.price))} (${service.duration}分鐘)`
  }));

  // 生成員工選項
  const staffOptions = [
    { value: '', label: t('dialogs.booking.noStaff', { ns: 'pages' }) },
    ...staff.map((s) => ({
      value: s.id,
      label: s.name
    }))
  ];

  // 生成狀態選項
  const statusOptions = [
    { value: 'PENDING', label: t('bookingStatus.PENDING', { ns: 'translation' }) },
    { value: 'CONFIRMED', label: t('bookingStatus.CONFIRMED', { ns: 'translation' }) },
    { value: 'COMPLETED', label: t('bookingStatus.COMPLETED', { ns: 'translation' }) },
    { value: 'CANCELLED', label: t('bookingStatus.CANCELLED', { ns: 'translation' }) }
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
          {/* 標題列 - 固定 */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditMode ? t('dialogs.booking.editTitle', { ns: 'pages' }) : t('dialogs.booking.addTitle', { ns: 'pages' })}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* 表單內容 - 可滾動區域 */}
          <form id="booking-form" onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto p-6 space-y-4">
            {submitError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-600 dark:text-red-400 text-sm">
                {submitError}
              </div>
            )}

            {/* 服務項目 - 移到最前面，因為需要先選服務才能看到可用時段 */}
            <FormField>
              <Label htmlFor="serviceId">{t('dialogs.booking.serviceItem', { ns: 'pages' })} <span className="text-red-500">*</span></Label>
              <input
                type="hidden"
                {...register('serviceId', { required: t('dialogs.booking.selectService', { ns: 'pages' }) })}
              />
              <CustomSelect
                id="serviceId"
                value={watchedServiceId}
                onChange={(value) => setValue('serviceId', value, { shouldValidate: true })}
                variant={errors.serviceId ? 'error' : 'default'}
                options={serviceOptions}
                disabled={isEditMode}
              />
              {errors.serviceId && <FormError>{errors.serviceId.message}</FormError>}
            </FormField>

            {/* 員工 (可選) */}
            <FormField>
              <Label htmlFor="staffId">{t('dialogs.booking.assignStaff', { ns: 'pages' })}</Label>
              <CustomSelect
                id="staffId"
                value={watchedStaffId}
                onChange={(value) => setValue('staffId', value)}
                options={staffOptions}
              />
            </FormField>

            {/* 預約日期時間 */}
            {useTimeSlotSelector && !isEditMode ? (
              <>
                {/* 日期選擇 */}
                <FormField>
                  <Label htmlFor="selectedDate">{t('pages:booking.dateTime.selectDate', { ns: 'pages' })} <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    id="selectedDate"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setSelectedTimeSlot(null); // 重置時段選擇
                    }}
                    min={(() => {
                      const today = new Date();
                      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                    })()}
                    max={(() => {
                      const maxDate = new Date();
                      maxDate.setMonth(maxDate.getMonth() + 3);
                      return `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}-${String(maxDate.getDate()).padStart(2, '0')}`;
                    })()}
                    className="[color-scheme:light] dark:[color-scheme:dark]"
                  />
                </FormField>

                {/* 時段選擇器 */}
                {selectedDate && watchedServiceId ? (
                  <FormField>
                    <TimeSlotSelector
                      storeId={storeId!}
                      serviceId={watchedServiceId}
                      selectedDate={selectedDate}
                      selectedSlot={selectedTimeSlot}
                      onSlotSelect={handleSlotSelect}
                      staffId={watchedStaffId || undefined}
                    />
                  </FormField>
                ) : (
                  watchedServiceId && (
                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                      {t('pages:booking.dateTime.pleaseSelectDate', { ns: 'pages' })}
                    </div>
                  )
                )}

                {/* 隱藏的 datetime-local 欄位（用於表單驗證和提交） */}
                <input
                  type="hidden"
                  {...register('date', { required: t('dialogs.booking.bookingTimeRequired', { ns: 'pages' }) })}
                />
                {errors.date && <FormError>{errors.date.message}</FormError>}
              </>
            ) : (
              <FormField>
                <Label htmlFor="date">{t('dialogs.booking.bookingTime', { ns: 'pages' })} <span className="text-red-500">*</span></Label>
                <Input
                  type="datetime-local"
                  id="date"
                  {...register('date', { required: t('dialogs.booking.bookingTimeRequired', { ns: 'pages' }) })}
                  variant={errors.date ? 'error' : 'default'}
                  className="[color-scheme:light] dark:[color-scheme:dark]"
                />
                {errors.date && <FormError>{errors.date.message}</FormError>}
              </FormField>
            )}

            {/* 客戶姓名 */}
            <FormField>
              <Label htmlFor="customerName">{t('dialogs.booking.customerName', { ns: 'pages' })} <span className="text-red-500">*</span></Label>
              <Input
                id="customerName"
                {...register('customerName', { required: t('dialogs.booking.customerNameRequired', { ns: 'pages' }) })}
                variant={errors.customerName ? 'error' : 'default'}
              />
              {errors.customerName && <FormError>{errors.customerName.message}</FormError>}
            </FormField>

            {/* 客戶電話 */}
            <FormField>
              <Label htmlFor="customerPhone">{t('dialogs.booking.customerPhone', { ns: 'pages' })} <span className="text-red-500">*</span></Label>
              <Input
                id="customerPhone"
                {...register('customerPhone', {
                  required: t('dialogs.booking.customerPhoneRequired', { ns: 'pages' })
                })}
                variant={errors.customerPhone ? 'error' : 'default'}
              />
              {errors.customerPhone && <FormError>{errors.customerPhone.message}</FormError>}
            </FormField>

            {/* 客戶 Email */}
            <FormField>
              <Label htmlFor="customerEmail">{t('dialogs.booking.customerEmail', { ns: 'pages' })}</Label>
              <Input
                type="email"
                id="customerEmail"
                {...register('customerEmail', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('dialogs.booking.emailInvalid', { ns: 'pages' })
                  }
                })}
                variant={errors.customerEmail ? 'error' : 'default'}
              />
              {errors.customerEmail && <FormError>{errors.customerEmail.message}</FormError>}
            </FormField>

            {/* 狀態 (只在編輯模式顯示) */}
            {isEditMode && (
              <FormField>
                <Label htmlFor="status">{t('dialogs.booking.status', { ns: 'pages' })}</Label>
                <CustomSelect
                  id="status"
                  value={watchedStatus}
                  onChange={(value) => setValue('status', value as any)}
                  options={statusOptions}
                />
              </FormField>
            )}

            {/* 備註 */}
            <FormField>
              <Label htmlFor="note">{t('dialogs.booking.note', { ns: 'pages' })}</Label>
              <textarea
                id="note"
                {...register('note')}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:border-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </FormField>
          </form>

          {/* 操作按鈕 - 固定在底部 */}
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-slate-700 flex-shrink-0">
            <Button
              type="button"
              onClick={handleClose}
              variant="secondary"
              disabled={!isEditMode && loading}
            >
              {t('dialogs.booking.cancel', { ns: 'pages' })}
            </Button>
            <Button
              type="submit"
              form="booking-form"
              disabled={!isEditMode && loading}
              className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {!isEditMode && loading ? t('dialogs.booking.processing', { ns: 'pages' }) : isEditMode ? t('dialogs.booking.update', { ns: 'pages' }) : t('dialogs.booking.add', { ns: 'pages' })}
            </Button>
          </div>
        </div>
    </div>
  );
};

export default BookingDialog;
